<template>
  <div class="language">
    <button class="language-button" ref="buttonRef" @click.stop="toggleMenu">
      <Icon name="ri:earth-line" />
      <span>{{ currentLocaleName }}</span>
    </button>
    <div class="language-menu" ref="menuRef" :class="{ show: isOpen }">
      <button v-for="locale in locales" :key="locale.code" class="language-menu__item" @click="switchLocale(locale.code)">
        <img :src="`/flags/4x3/${locale.code}.svg`" />
        <span>{{ locale.name }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { autoUpdate, computePosition, offset, shift } from "@floating-ui/dom"
  import fetchApi from "~/utils/fetchApi"
  import type Locale from "~/interfaces/locale"

  const buttonRef = ref<HTMLElement | null>(null)
  const menuRef = ref<HTMLElement | null>(null)
  const isOpen = ref(false)

  const { data: locales } = await useAsyncData<Locale[]>("locales", () => fetchApi<Locale[]>("i18n/locales"))
  const { locale, setLocale } = useI18n()

  const currentLocaleName = computed(() => {
    const current = locales.value?.find((l) => l.code === locale.value)
    return current?.name || "Türkçe"
  })

  let cleanup: () => void

  const toggleMenu = () => {
    isOpen.value = !isOpen.value
    if (isOpen.value && buttonRef.value && menuRef.value) {
      cleanup = autoUpdate(buttonRef.value, menuRef.value, () => {
        if (!buttonRef.value || !menuRef.value) return
        computePosition(buttonRef.value, menuRef.value, {
          placement: "bottom-end",
          middleware: [offset(4), shift()]
        }).then(({ x, y }) => {
          Object.assign(menuRef.value!.style, {
            left: `${x}px`,
            top: `${y}px`
          })
        })
      })
    } else if (cleanup) {
      cleanup()
    }
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.value && !menuRef.value.contains(event.target as Node) && buttonRef.value && !buttonRef.value.contains(event.target as Node)) {
      isOpen.value = false
      if (cleanup) cleanup()
    }
  }

  const switchLocale = async (code: string) => {
    isOpen.value = false
    if (cleanup) cleanup()
    await setLocale(code)
  }

  onMounted(() => {
    document.addEventListener("click", handleClickOutside)
    document.addEventListener("scroll", () => {
      isOpen.value = false
      if (cleanup) cleanup()
    })
  })

  onUnmounted(() => {
    document.removeEventListener("click", handleClickOutside)
    if (cleanup) cleanup()
  })
</script>

<style lang="scss" scoped>
  .language {
    &-button {
      @apply font-display inline-flex w-fit items-center justify-center gap-2.5 bg-transparent border-none text-center;
      @apply text-inherit text-3 font-bold leading-4 uppercase p-4 -m-4;
      letter-spacing: 3px;

      :deep(svg) {
        @apply text-6;
      }
      span {
        @apply -mb-0.5;
      }
    }
    &-menu {
      @apply flex flex-col absolute opacity-0 invisible transition-all duration-200;
      background: #f2f0e9;
      width: 130px;
      border: 1px solid #e5e5e5;

      &__item {
        @apply flex gap-2 px-2 py-1.5 items-center text-left border-none bg-transparent;

        img {
          @apply h-4;
        }
        span {
          @apply text-sm font-medium text-black/80;
        }

        &:not(:last-child) {
          @apply border-b border-b-solid border-[#e5e5e5];
        }

        &:hover {
          @apply bg-[#e5e5e5]/40;
        }
      }

      &.show {
        @apply opacity-100 visible;
      }
    }

    @include media("md") {
      &-button {
        @apply gap-2 text-3 flex-row-reverse py-2 -my-2 px-0 mx-0;
        letter-spacing: 2px;

        :deep(svg) {
          @apply text-5;
        }
      }
    }
  }
</style>
